import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import {
  AlertColor,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
} from '@mui/material';
import { Box } from '@mui/system';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import ConsentTableRow from './ConsentTableRow';
import MediumTypography from '../../components/formlib/MediumTypography';
import {
  getConsentDetails,
  ConsentListData,
  getConsentList,
  downloadConsent,
  ConsentType,
  createConsents,
  deleteConsent,
} from '../../services/configApi/forms/Consent/ConsentServices';
import { useNavigate } from 'react-router';
import { CONSENT_RELEASE_INFO, FACESHEET } from '../../routes/Routing';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import TitleText from '../../components/formlib/TitleText';
import CustomBackArrow from '../../components/imagepickers/backArrow';
import SingleLine from '../../components/formlib/SingleLine';
import { ReactComponent as Close } from '../../assets/images/x.svg';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import dayjs from 'dayjs';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import EmptyScreen from '../../components/shared/EmptyScreen';
import AppPagination from '../../components/shared/AppPagination';
import ShareDownloadModal from '../../components/formlib/modal/ShareDownloadModal';
import { LookUpContext } from '../../context/LookUpContextProvider';
import { formatName } from '../../utils/nameUtils';
import { ConsentContext } from './ConsentProvider';
import { checkOffline } from '../../services/Offline';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import ModalPopup from '../../components/formlib/ModalPopup';
import LanguageSelectionDialog from '../../components/shared/LanguageSelectionDialog';
import { useMultiplePermissions } from '../../hooks/usePermissions';
import { REFERRAL_STATUS_KEY } from '../../services/Constant';
import { getClientDocumentFileName } from '../../utils/fileUtils';

type Order = 'asc' | 'desc';

export const consentTypes: ConsentType[] = [
  {
    id: 'PriorWrittenNoticeForm',
    label: 'Prior Written Notice Form',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'GRI',
    label: 'General Release of Information',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'Assessment',
    label: 'Consent for Evaluation / Assessment',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'Telehealth',
    label: 'Consent for Telehealth',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'SavingsAcc',
    label:
      'Health Savings Account (HSA) / Flexible Spending Account (FSA) Acknowledgment',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'CommunicateWithMedical',
    label: 'Consent to communicate with Medical Professionals using THOM Email',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'CommunicateWithFamily',
    label:
      'Consent to communicate with Family using THOM email and ThomConnect',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'NoticeOfPrivacyPractices',
    label: 'Notice of Privacy Practices',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'LegalReleaseofRecords',
    label: 'Legal Release of Records',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'InsuranceConsent',
    label: 'Insurance Consent',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'SspShare',
    label: 'Consent to Share',
    isSelected: false,
    uiVersion: 1,
  },
  {
    id: 'SspCommunicate',
    label: 'SSP Consent to Communicate',
    isSelected: false,
    uiVersion: 1,
  },
];

interface Data {
  consentName: string;
  consentVersion: number;
  consentDate: string;
  // expirationDate: string;
  status: string;
  permissionGrantedTo: string;
  actions: string;
}

const headCells: HeadCell<Data>[] = [
  {
    id: 'consentName',
    labelId: 'ConsentForms.listHeaderConsents',
    defaultLabelId: 'Consent Name',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'consentDate',
    labelId: 'ConsentForms.listHeaderConsentDate',
    defaultLabelId: 'Consent Date',
    numeric: false,
    requiredSorting: false,
  },
  // {
  //   id: 'expirationDate',
  //   labelId: 'ConsentForms.listHeaderExpiryDate',
  //   defaultLabelId: 'Expiry Date',
  //   numeric: false,
  //   requiredSorting: false,
  // },
  {
    id: 'status',
    labelId: 'ConsentForms.listHeaderStatus',
    defaultLabelId: 'Status',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'permissionGrantedTo',
    labelId: 'ConsentForms.labelPermissionTo',
    defaultLabelId: 'Permission Granted to',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'actions',
    labelId: 'ConsentForms.listHeaderActions',
    defaultLabelId: 'Actions',
    numeric: false,
    requiredSorting: false,
  },
];

const ConsentDashBoard = () => {
  const [consentList, setConsentList] = useState<ConsentListData[]>([]);
  const [consentEntireList, setConsentEntireList] = useState<ConsentListData[]>(
    [],
  );
  const [consentDataCount, setConsentDataCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const { toggleLoader } = React.useContext(LoaderContext) as LoaderContextType;
  const [order, setOrder] = useState<Order>();
  const [orderBy, setOrderBy] = useState<keyof Data>();

  const navigate = useNavigate();

  const [open, setOpen] = React.useState(false);
  const [toastrVariable, setToastrVariable] =
    React.useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = React.useState('');
  const [toastrId, setToastrId] = React.useState('');
  const [clientName, setClientName] = React.useState('Consent');
  const [dialogFlag, setDialogFlag] = useState(false);
  const [filteredConsentListsType, setFilteredConsentListsType] = useState<
    ConsentType[]
  >([]);

  const clientIdentity = localStorage.getItem('ClientId');
  const [shareModal, setShareModal] = useState<boolean>(false);
  const [consentDeleteModal, setConsentDeleteModal] = useState<boolean>(false);
  const [openDownloadLanguageModal, setOpenDownloadLanguageModal] =
    useState<boolean>(false);
  const [downloadRow, setDownloadRow] = useState<ConsentListData>();
  const [selectedRowItem, setSelectedRowItem] = useState<ConsentListData>();
  const [offlineMode, setOfflineMode] = useState(false);
  const [APICallSuccessOnZeroCount, setAPICallSuccessOnZeroCount] =
    useState(false);

  const {
    updateConsentJSON,
    updateConsentVersionNo,
    updateConsentUiVersionNo,
  } = useContext(ConsentContext);

  const { lookupsDemographics } = useContext(LookUpContext);

  const permissions = useMultiplePermissions([
    'backend.clients',
    'backend.in_active_referrals',
  ]);
  const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
  const hasEditPermission =
    referralStatus === 'Inactive'
      ? permissions['backend.clients'].canEdit &&
        permissions['backend.in_active_referrals'].canEdit
      : permissions['backend.clients'].canEdit ||
        permissions['backend.in_active_referrals'].canEdit;

  const ITEMS_PER_PAGE = 10;

  useLayoutEffect(() => {
    const getOfflineMode = async () => {
      const offlineFlag = await checkOffline();
      setOfflineMode(offlineFlag);
    };
    getOfflineMode();
  });

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createConsentForms = (forms: ConsentType[]) => {
    toggleLoader(true);
    createConsents(forms)
      .then(() => {
        setDialogFlag(false);
        if (clientIdentity) getConsentListDetails(clientIdentity);
      })
      .catch((error) => {
        toggleLoader(false);
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('ConsentForms.concentCreateError');
          setToastrDefaultMessage('Failed to create Consent forms');
        }
      });
  };

  const updateConsentCheck = (id: string, checked: boolean) => {
    setFilteredConsentListsType((prev) =>
      prev?.map((item) =>
        item.id === id
          ? {
              ...item,
              isSelected: checked,
              uiVersion: 3,
            }
          : item,
      ),
    );
  };

  const getConsentDate = (
    selectedConsent: string,
    selectedConsentId: string,
    selectedConsentVersion: number,
  ) => {
    toggleLoader(true);
    getConsentDetails(
      selectedConsent,
      selectedConsentVersion,
      selectedConsentId,
    )
      .then(async (response) => {
        if (response) {
          if (response.signature.length == 0) {
            response.signature = [
              {
                id: '',
                name: '',
                relationship: '',
                email: '',
                signature: '',
                date: '',
                rowKey: '',
                verbal: false,
                paperConsent: false,
                firstName: '',
                lastName: '',
                isLegal: false,
                isPrimary: false,
                isGuardian: false,
              },
            ];
          }
          updateConsentJSON(response);
          updateConsentVersionNo(selectedConsentVersion);
          setTimeout(() => {
            navigate(CONSENT_RELEASE_INFO);
            toggleLoader(false);
          }, 0);
        }
      })
      .catch(async (error) => {
        toggleLoader(false);
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('ConsentForms.concentLoadError');
          setToastrDefaultMessage('Failed to load Consent details');
        }
      });
  };

  function compareValues<T>(a: T, b: T, orderType: Order): number {
    if (orderType === 'asc') {
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    } else {
      if (a < b) return 1;
      if (a > b) return -1;
      return 0;
    }
  }

  function sortConsents(
    consents: ConsentListData[],
    typeOrder: Order,
    versionOrder: Order,
    dateOrder: Order,
  ): ConsentListData[] {
    return consents.sort((a, b) => {
      const typeComparison = compareValues(
        a.consentType,
        b.consentType,
        typeOrder,
      );
      if (typeComparison !== 0) return typeComparison;

      const versionComparison = compareValues(
        a.version,
        b.version,
        versionOrder,
      );
      if (versionComparison !== 0) return versionComparison;

      return compareValues(
        new Date(a.consentDate).getTime(),
        new Date(b.consentDate).getTime(),
        dateOrder,
      );
    });
  }

  const getConsentListDetails = (clientId: string) => {
    if (clientId) {
      getConsentList('consentDate', 'desc'.toUpperCase(), 'consent')
        .then((response) => {
          const sortedConsents = sortConsents(
            response.consents,
            'asc',
            'desc',
            'desc',
          );

          setConsentEntireList(sortedConsents);
          const start = page * ITEMS_PER_PAGE;
          const end = start + ITEMS_PER_PAGE;
          setConsentList(sortedConsents.slice(start, end));
          setFilteredConsentListsType(consentTypes);
          setConsentDataCount(response.totalConsentsCount);
          if (sortedConsents.slice(start, end).length === 0) {
            setAPICallSuccessOnZeroCount(true);
          }
          toggleLoader(false);
        })
        .catch((error) => {
          toggleLoader(false);
          setOpen(true);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('ConsentForms.concentLoadError');
            setToastrDefaultMessage('Failed to load Consent details');
          }
        });
    }
  };

  useEffect(() => {
    if (lookupsDemographics !== null) {
      const fullName = formatName(
        lookupsDemographics.firstName,
        lookupsDemographics.middleName,
        lookupsDemographics.lastName,
        lookupsDemographics.suffix,
      );
      setClientName(fullName);
    }

    if (clientIdentity) {
      toggleLoader(true);
      getConsentListDetails(clientIdentity);
    }
  }, []);

  useEffect(() => {
    if (clientIdentity) {
      const sortedConsents = sortConsents(
        consentEntireList,
        'asc',
        'desc',
        'desc',
      );
      const start = page * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE;
      setConsentList(sortedConsents.slice(start, end));
    }
  }, [page]);

  const onViewClick = (selectedRow: ConsentListData) => {
    if (clientIdentity) updateConsentUiVersionNo(selectedRow.uiVersion);
    // neeed to update
    getConsentDate(
      selectedRow.consentType,
      selectedRow.consentId,
      selectedRow.version,
    );
  };

  const onDownloadClick = (selectedRow: ConsentListData) => {
    setDownloadRow(selectedRow);
    setOpenDownloadLanguageModal(true);
  };

  const handleDownload = (selectedRow: ConsentListData, language: string) => {
    toggleLoader(true);
    const localTimezone = dayjs.tz.guess();
    downloadConsent(
      selectedRow.consentType,
      selectedRow.consentId,
      localTimezone,
      language,
    )
      .then((response) => {
        const blob = new Blob([response], { type: 'application/pdf' });

        // Create a Blob URL
        const blobUrl = URL.createObjectURL(blob);

        // Open the Blob URL in a new tab
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.target = '_blank';
        anchor.download = getClientDocumentFileName(
          `${selectedRow.consentName}.pdf`,
          selectedRow.consentDate || null,
        ); // Set desired file name here

        // Append anchor to document body
        document.body.appendChild(anchor);

        // Trigger anchor click
        anchor.click();

        window.open(blobUrl, '_blank');

        // Clean up after use
        URL.revokeObjectURL(blobUrl);
        document.body.removeChild(anchor);

        toggleLoader(false);
      })
      .catch((error) => {
        toggleLoader(false);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('ConsentForms.downloadErrorMsg');
          setToastrDefaultMessage('Failed to download');
        }
      });
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const onShareClick = (selectedRow: ConsentListData) => {
    setShareModal(true);
    setSelectedRowItem(selectedRow);
  };

  const onDeleteTrigger = (selectedRow: ConsentListData) => {
    setConsentDeleteModal(true);
    setSelectedRowItem(selectedRow);
  };

  const onDeleteClick = () => {
    if (selectedRowItem?.consentId === undefined) {
      return;
    }

    toggleLoader(true);
    deleteConsent(selectedRowItem.consentId)
      .then(() => {
        setConsentDeleteModal(false);
        setOpen(true);
        setToastrVariable('success');
        setToastrId('ConsentForms.deleteSuccessMsg');
        setToastrDefaultMessage('Consent form deleted successfully');

        if (clientIdentity) {
          getConsentListDetails(clientIdentity);
        }
      })
      .catch((error) => {
        setOpen(true);
        setToastrVariable('error');
        setConsentDeleteModal(false);
        toggleLoader(false);

        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('ConsentForms.deleteErrorMsg');
          setToastrDefaultMessage('Failed to delete consent form');
        }
      });
  };

  return (
    <>
      <Box component="main">
        <Box component="section">
          <ModalPopup
            open={consentDeleteModal}
            onClose={() => setConsentDeleteModal(false)}
            description={'Are you sure you want to delete this consent form?'}
            labelId1="Clientpage.cancelbtn"
            defaultDescription="Are you sure you want to delete this consent form?"
            negativeActionLabel="cancelText"
            labelId2="Clientpage.Okbtn"
            positiveActionLabel="deleteText"
            onOk={() => {
              onDeleteClick();
            }}
            onCancel={() => {
              setConsentDeleteModal(false);
            }}
          />
          <SnackBarComponent
            open={open}
            handleClose={handleClose}
            successOrError={toastrVariable}
            labelId={toastrId}
            defaultMessageId={toastrDefaultMessage}
          />
          <Dialog open={dialogFlag} fullWidth maxWidth="sm">
            <DialogTitle className="pb-none">
              <Box
                className="mt-sm mb-sm"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center', // Align items vertically in the center
                }}
              >
                <MediumTypography
                  labelid={'forms.titleText'}
                  sxProps={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                  }}
                  defaultlabel="Forms"
                />
                <Close
                  style={{
                    marginLeft: 'auto', // Move the Close button to the right side
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setFilteredConsentListsType((previous) => {
                      return previous.map((item) => {
                        return {
                          ...item,
                          isSelected: false,
                        };
                      });
                    });
                    setDialogFlag(false);
                  }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              {filteredConsentListsType?.map((eventProps: ConsentType) => {
                return (
                  <Box sx={{ marginY: '10px' }} key={eventProps.id}>
                    <Box className="flex__ align__items__center">
                      <CheckBoxComponent
                        ischecked={eventProps.isSelected}
                        CheckHandleChange={(e) => {
                          updateConsentCheck(eventProps.id, e.target.checked);
                        }}
                      />
                      <MediumTypography label={eventProps.label} />
                    </Box>

                    <SingleLine
                      sxContainerProps={{ marginY: '8px' }}
                      lineWidth="100%"
                    />
                  </Box>
                );
              })}
              {filteredConsentListsType?.length === 0 && (
                <Box className="flex__ align__items__center">
                  <MediumTypography
                    labelid="forms.noFormsText"
                    defaultlabel="No more forms available to add."
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions className="mt-sm mb-sm mr-xs">
              <ButtonComponent
                type="submit"
                variantType="contained"
                labelId="IFSP.Add"
                defaultLabelId="Add"
                disabled={
                  !filteredConsentListsType.find(
                    (consent) => consent.isSelected,
                  )
                }
                className="btn-primary btn-submit"
                onClick={() => {
                  const selectedConsents = filteredConsentListsType.filter(
                    (consent) => consent.isSelected,
                  );
                  if (selectedConsents.length > 0) {
                    createConsentForms(selectedConsents);
                  }
                }}
              />
            </DialogActions>
          </Dialog>
          <Box component="div" className="rowContainer">
            <Grid container>
              <Grid item xs={6} lg={6}>
                <Box className="flex__ align__items__center">
                  <Box className="flex__ ">
                    <CustomBackArrow onClick={() => navigate(FACESHEET)} />
                  </Box>

                  <TitleText
                    className="ml-md"
                    Sxprops={{
                      ml: 1,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      width: '600px',
                    }}
                    label={clientName}
                  />
                </Box>
              </Grid>
              {consentList.length > 0 &&
                hasEditPermission &&
                checkPermissionForFeature(
                  'backend.consents',
                  'addPermission',
                ) && (
                  <Grid item xs={6} lg={6} className="text-align-end">
                    <ButtonComponent
                      className="btn-primary btn-submit"
                      variantType="contained"
                      type="submit"
                      labelId="ConsentForms.buttonLabel"
                      defaultLabelId="Add Consents"
                      onClick={() => setDialogFlag(true)}
                    />
                  </Grid>
                )}
            </Grid>
          </Box>

          {consentList.length > 0 && (
            <Card>
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableHeader
                    className="listDataTableHead"
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    headerNames={headCells}
                    checkBoxRequired={false}
                  />
                  <TableBody className="tableRowcss">
                    {consentList.length !== 0 &&
                      consentList.map((consentData) => (
                        <ConsentTableRow
                          key={consentData.consentId}
                          data={consentData}
                          onView={onViewClick}
                          onDownload={onDownloadClick}
                          onShare={onShareClick}
                          onDelete={onDeleteTrigger}
                          offlineFlag={offlineMode}
                          hasEditPermission={hasEditPermission}
                        />
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
          {consentDataCount > ITEMS_PER_PAGE && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <AppPagination
                pageNumber={page}
                paginationCount={consentDataCount}
                handleChangePage={(_event, newPage) => {
                  if (newPage - 1 !== page) {
                    setPage(newPage - 1);
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {shareModal && (
        <ShareDownloadModal
          screenName={'consent'}
          modalVisible={shareModal}
          closeModal={() => setShareModal(false)}
          consentData={selectedRowItem}
        />
      )}

      <LanguageSelectionDialog
        open={openDownloadLanguageModal}
        onClose={() => {
          setOpenDownloadLanguageModal(false);
          setDownloadRow(undefined);
        }}
        onConfirm={(language) => {
          setOpenDownloadLanguageModal(false);
          if (downloadRow) {
            handleDownload(downloadRow, language);
          }
          setDownloadRow(undefined);
        }}
      />

      {APICallSuccessOnZeroCount && consentList.length === 0 && (
        <Box component="main" className="mb-md">
          <Box component="section">
            <EmptyScreen
              titleLabelId="ConsentForms.emptyMessage"
              defaultTitleText="No Consent Forms added yet"
              buttonLabelId="ConsentForms.buttonLabel"
              defaultButtonTitleText="Add Consents"
              showButton={
                hasEditPermission &&
                checkPermissionForFeature('backend.consents', 'addPermission')
              }
              onButtonClick={() => {
                setDialogFlag(true);
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default ConsentDashBoard;
